/**
 * Pure part of the deliverables-regeneration helpers (2026-09-07): no axios, no API
 * module. `AnalysisDashboard.jsx` imports from HERE so that every component (and Jest
 * suite) pulling in the dashboard does not drag axios' ESM build into CRA's Jest, which
 * does not transform it (`QueuedRunBadge.test.jsx` broke the moment the dashboard
 * imported the axios-backed helper). `regenerateDeliverables.js` re-exports this.
 *
 * Scope selection (2026-09-07, "regenerate only the deliverables I need"): the helpers
 * below describe a run's collections (analysis type + P/N direction), group them for the
 * modal's Scope section, implement the All / per-type / per-direction quick toggles and
 * resolve the explicit `collections` list the modal submits.
 */

/** True when the tile may offer the action: a completed run that has a manifest. */
export function isRunRegenerable(run, regenerableRunIds) {
  if (!run || !run.run_id) return false;
  const status = String(run.status || "").trim().toLowerCase();
  if (status !== "completed") return false;
  return Boolean(regenerableRunIds && regenerableRunIds[run.run_id]);
}

export const COLLECTION_DIRECTIONS = ["P", "N"];

/**
 * `{route}_{code}_{path_id}_{TYPE}_{DIR}` -> `{ name, analysisType, direction }`.
 * The same rule the backend's run_manifest.collection_analysis_type / collection_direction
 * apply; an unparsable name yields empty type / direction (still listed, still selectable).
 */
export function parseCollectionName(name) {
  const text = String(name || "").trim();
  const parts = text.split("_").filter(Boolean);
  const analysisType = parts.length >= 2 ? parts[parts.length - 2].toUpperCase() : "";
  const last = parts.length ? parts[parts.length - 1].toUpperCase() : "";
  const direction = COLLECTION_DIRECTIONS.includes(last) ? last : "";
  return { name: text, analysisType, direction };
}

/**
 * The run's collections as `[{ name, analysisType, direction }]` in manifest order.
 * Prefers the manifest GET's `collection_details` (backend-derived); falls back to
 * parsing `collections` for a manifest served by an older backend.
 */
export function describeRunCollections(manifest) {
  const details = manifest && Array.isArray(manifest.collection_details) ? manifest.collection_details : null;
  if (details && details.length) {
    return details
      .filter((d) => d && String(d.name || "").trim())
      .map((d) => ({
        name: String(d.name).trim(),
        analysisType: String(d.analysis_type || parseCollectionName(d.name).analysisType || "").toUpperCase(),
        direction: String(d.direction || parseCollectionName(d.name).direction || "").toUpperCase(),
      }));
  }
  const names = manifest && Array.isArray(manifest.collections) ? manifest.collections : [];
  const seen = new Set();
  const out = [];
  names.forEach((n) => {
    const parsed = parseCollectionName(n);
    if (!parsed.name || seen.has(parsed.name)) return;
    seen.add(parsed.name);
    out.push(parsed);
  });
  return out;
}

/** `[{ analysisType, items: [...] }]` grouped by type in first-seen order. */
export function groupCollectionsByType(details) {
  const groups = [];
  const byType = new Map();
  (details || []).forEach((d) => {
    const key = d.analysisType || "OTHER";
    if (!byType.has(key)) {
      const group = { analysisType: key, items: [] };
      byType.set(key, group);
      groups.push(group);
    }
    byType.get(key).items.push(d);
  });
  return groups;
}

/** The distinct directions present, in P/N order (then anything else). */
export function collectionDirections(details) {
  const present = new Set((details || []).map((d) => d.direction).filter(Boolean));
  return [...COLLECTION_DIRECTIONS.filter((d) => present.has(d)), ...[...present].filter((d) => !COLLECTION_DIRECTIONS.includes(d))];
}

/**
 * Quick-toggle rule shared by the All / per-type / per-direction buttons: when every
 * name of the group is selected, deselect the group; otherwise select the whole group.
 * Returns a NEW array of selected names (input order preserved, group names appended).
 */
export function toggleScopeGroup(selectedNames, groupNames) {
  const selected = new Set((selectedNames || []).map((n) => String(n || "").trim()).filter(Boolean));
  const group = (groupNames || []).map((n) => String(n || "").trim()).filter(Boolean);
  if (!group.length) return [...selected];
  const allOn = group.every((n) => selected.has(n));
  if (allOn) {
    group.forEach((n) => selected.delete(n));
  } else {
    group.forEach((n) => selected.add(n));
  }
  return [...selected];
}

/**
 * The scope the modal submits: the selected collections in MANIFEST order (never the
 * click order), plus the types / directions they span and whether that is the whole run.
 * Unknown names are dropped. `{ collections: [], all: false }` for an empty selection.
 */
export function resolveScopeSelection(details, selectedNames) {
  const all = (details || []).map((d) => d.name);
  const wanted = new Set((selectedNames || []).map((n) => String(n || "").trim()).filter(Boolean));
  const collections = all.filter((n) => wanted.has(n));
  const chosen = (details || []).filter((d) => wanted.has(d.name));
  const analysisTypes = [...new Set(chosen.map((d) => d.analysisType).filter(Boolean))];
  const directions = collectionDirections(chosen);
  return {
    collections,
    all: all.length > 0 && collections.length === all.length,
    analysisTypes,
    directions,
  };
}
